import { useEffect, useId, useRef, useState } from "react";
import {
  getUpcomingLabs,
  upsertLabDeliveryRegistration,
  upsertSubscriber,
} from "../firebase";
import { formatLabDate, formatLabTimeRange } from "../utils/datetime";
import { US_STATE_ABBREVIATIONS } from "../utils/usStates";
import AddToCalendarButtons from "./AddToCalendarButtons";

const ROLE_OPTIONS = [
  "Principal",
  "Assistant Principal",
  "Superintendent",
  "Other school/district leader",
];

const STORAGE_KEY = "school_leader_ai_lab_registration_form";

const initialForm = {
  firstName: "",
  email: "",
  role: ROLE_OPTIONS[0],
  state: "",
};

const readStoredForm = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(STORAGE_KEY);
    if (!rawValue) {
      return null;
    }

    const parsed = JSON.parse(rawValue);
    return {
      firstName: parsed.firstName || "",
      email: parsed.email || "",
      role: parsed.role || ROLE_OPTIONS[0],
      state: parsed.state || "",
    };
  } catch (error) {
    console.error("Could not parse saved registration form values:", error);
    return null;
  }
};

function RegistrationForm({
  nextLab,
  preferredLab = null,
  source,
  submitLabel = "Get my free invite",
  preSubmitNote = "",
  variant = "default",
}) {
  const [formData, setFormData] = useState(() => ({
    ...initialForm,
    ...readStoredForm(),
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [confirmation, setConfirmation] = useState(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [showCalendarOptions, setShowCalendarOptions] = useState(false);
  const closeButtonRef = useRef(null);
  const modalTitleId = useId();
  const calendarOptionsId = useId();

  const isAssociationVariant = variant === "association";

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
  }, [formData]);

  useEffect(() => {
    if (!isConfirmationOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsConfirmationOpen(false);
        setShowCalendarOptions(false);
      }
    };

    window.addEventListener("keydown", handleEscape);
    window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isConfirmationOpen]);

  const normalizedSource = {
    sourceType: source?.sourceType || "public",
    sourceSlug: source?.sourceSlug || "main-hub",
    sourceName: source?.sourceName || "AI Lab Hub",
  };
  const associationBenefitCopy =
    isAssociationVariant && normalizedSource.sourceType === "association"
      ? `This AI Lab is provided free for ${normalizedSource.sourceName} members.`
      : "";
  const emailFieldLabel = isAssociationVariant ? "School email" : "Work email";
  const formMicrocopy = isAssociationVariant
    ? "One form covers all future Labs. Takes about 15 seconds. No spam. Unsubscribe anytime."
    : "Takes about 30 seconds. No spam. Unsubscribe anytime.";

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: "", message: "" });
    setConfirmation(null);
    setIsConfirmationOpen(false);
    setShowCalendarOptions(false);
    setIsSubmitting(true);

    const trimmedFirstName = formData.firstName.trim();
    const normalizedEmail = formData.email.trim().toLowerCase();
    const normalizedState = formData.state.trim();

    try {
      const selectedLab = preferredLab
        ? preferredLab
        : (await getUpcomingLabs(1))[0] || nextLab || null;

      const subscriber = await upsertSubscriber({
        firstName: trimmedFirstName,
        email: normalizedEmail,
        role: formData.role,
        state: normalizedState,
        ...normalizedSource,
        active: true,
      });

      const deliveryResult = await upsertLabDeliveryRegistration({
        firstName: subscriber.firstName || trimmedFirstName,
        email: subscriber.email || normalizedEmail,
        role: subscriber.role || formData.role,
        state: subscriber.state || normalizedState,
        labId: selectedLab ? selectedLab.id : null,
        sourceType: subscriber.sourceType || normalizedSource.sourceType,
        sourceSlug: subscriber.sourceSlug || normalizedSource.sourceSlug,
        sourceName: subscriber.sourceName || normalizedSource.sourceName,
        requestConfirmation: true,
      });

      const alreadyRegistered = deliveryResult.alreadyRegistered === true;
      const emailSent = Boolean(deliveryResult.registrationId);

      setFormData((previous) => ({
        ...previous,
        firstName: trimmedFirstName,
        email: normalizedEmail,
        state: normalizedState,
      }));

      setConfirmation({
        firstName: trimmedFirstName,
        email: normalizedEmail,
        lab: selectedLab,
        emailSent,
        alreadyRegistered,
      });
      setShowCalendarOptions(false);
      setIsConfirmationOpen(true);
    } catch (submitError) {
      console.error("Registration error:", submitError);
      setStatus({
        type: "error",
        message:
          "We couldn't submit your registration right now. Please try again in a moment.",
      });
      setConfirmation(null);
      setShowCalendarOptions(false);
      setIsConfirmationOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeConfirmation = () => {
    setIsConfirmationOpen(false);
    setShowCalendarOptions(false);
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      closeConfirmation();
    }
  };

  const handleCalendarPrimaryClick = () => {
    setShowCalendarOptions((previous) => !previous);
  };

  return (
    <>
      <form className="form" onSubmit={handleSubmit}>
        <div className="form-grid">
          <label className="form-field" htmlFor="firstName">
            <span>First name</span>
            <input
              id="firstName"
              name="firstName"
              type="text"
              value={formData.firstName}
              onChange={handleChange}
              required
            />
          </label>

          <label className="form-field" htmlFor="email">
            <span>{emailFieldLabel}</span>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </label>

          <label className="form-field" htmlFor="role">
            <span>Role</span>
            <select id="role" name="role" value={formData.role} onChange={handleChange} required>
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>
                  {role}
                </option>
              ))}
            </select>
          </label>

          <label className="form-field" htmlFor="state">
            <span>State</span>
            <select
              id="state"
              name="state"
              value={formData.state}
              onChange={handleChange}
              required
            >
              <option value="">Select your state</option>
              {US_STATE_ABBREVIATIONS.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </label>
        </div>

        {preSubmitNote ? <p className="form-pre-submit-note">{preSubmitNote}</p> : null}

        <button className="btn btn-primary" type="submit" disabled={isSubmitting}>
          {isSubmitting ? "Submitting..." : submitLabel}
        </button>
        <p className="form-microcopy">{formMicrocopy}</p>

        {status.type === "error" && status.message ? (
          <p className={`status-message ${status.type}`}>{status.message}</p>
        ) : null}
      </form>

      {confirmation && isConfirmationOpen ? (
        <div
          className="registration-confirmation-overlay"
          onClick={handleOverlayClick}
          role="presentation"
        >
          <section
            className={`card registration-confirmation ${
              isAssociationVariant ? "association-confirmation" : ""
            }`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={modalTitleId}
          >
            <button
              ref={closeButtonRef}
              type="button"
              className="confirmation-close-btn"
              aria-label="Close confirmation"
              onClick={closeConfirmation}
            >
              X
            </button>

            {isAssociationVariant ? (
              <p className="confirmation-success-badge">Success</p>
            ) : null}

            <h3 id={modalTitleId}>
              {confirmation.alreadyRegistered
                ? "You're already registered for this AI Lab"
                : "You're registered for the AI Lab"}
            </h3>
            <p>
              {confirmation.firstName}, you are registered with{" "}
              <strong>{confirmation.email}</strong>.
            </p>
            <ul className="confirmation-next-steps">
              <li>Add this to your calendar.</li>
              <li>Check your email for details and reminders.</li>
            </ul>

            {confirmation.alreadyRegistered ? (
              <p className="confirmation-already-note">
                You're all set. If it's not already on your calendar, add it now:
              </p>
            ) : null}

            {confirmation.lab ? (
              <>
                <div className="confirmation-when-block">
                  <p className="confirmation-when-label">When</p>
                  <p className="eyebrow">{formatLabDate(confirmation.lab.dateTime)}</p>
                  <p className="lab-time">
                    {formatLabTimeRange(
                      confirmation.lab.dateTime,
                      confirmation.lab.durationMinutes
                    )}
                  </p>
                  <p className="confirmation-title">{confirmation.lab.title}</p>
                </div>

                <div className="confirmation-calendar-cta">
                  <button
                    type="button"
                    className="btn btn-primary confirmation-calendar-primary"
                    onClick={handleCalendarPrimaryClick}
                    aria-expanded={showCalendarOptions}
                    aria-controls={calendarOptionsId}
                  >
                    Add to my calendar
                  </button>

                  {showCalendarOptions ? (
                    <div id={calendarOptionsId} className="confirmation-calendar-options">
                      <p className="confirmation-calendar-options-label">
                        Choose your calendar:
                      </p>
                      <AddToCalendarButtons lab={confirmation.lab} variant="compact" />
                    </div>
                  ) : null}
                </div>

                <div className="confirmation-zoom-block">
                  <p className="confirmation-zoom-label">Zoom link for the day of the Lab</p>
                  <p className="confirmation-zoom-url">
                    <a href={confirmation.lab.zoomLink} target="_blank" rel="noreferrer">
                      {confirmation.lab.zoomLink}
                    </a>
                  </p>
                  <p className="confirmation-zoom-note">
                    Use this link at the time of the Lab. We'll also send it in your
                    reminder email.
                  </p>
                </div>
              </>
            ) : (
              <p>
                New date coming soon. We'll email you as soon as the next monthly AI
                Lab is scheduled, and you'll automatically receive the invite and
                recording.
              </p>
            )}

            {associationBenefitCopy ? (
              <p className="confirmation-association-note">{associationBenefitCopy}</p>
            ) : null}

            {confirmation.alreadyRegistered ? (
              confirmation.emailSent ? (
                <p className="muted-copy">
                  You're already registered. We emailed the details again so you have
                  everything handy.
                </p>
              ) : (
                <p className="muted-copy">
                  You're already registered. We saved your spot, but your email may be
                  delayed.
                </p>
              )
            ) : confirmation.emailSent ? (
              <p className="muted-copy">We've also emailed you the details.</p>
            ) : (
              <p className="muted-copy">
                We saved your spot, but your confirmation email may be delayed.
              </p>
            )}
          </section>
        </div>
      ) : null}
    </>
  );
}

export default RegistrationForm;
